import React from 'react';
import {Icon, Text} from '@/ui-kits';

interface TemporaryVisibilityCellProps {
  /** The unique identifier for this item (used to track visibility state) */
  id: number | string;
  /** The actual value to display when visible (can be processed/decrypted) */
  value: string;
  /** Whether this item is currently visible */
  isVisible: boolean;
  /** Function to toggle visibility */
  onToggle: (id: number | string) => void;
  /** Optional function to transform the value before display (e.g., decryption) */
  valueFormatter?: (value: string) => string;
  /** Custom className for the text container when visible */
  textClassName?: string;
  /** Custom className for the eye icon button */
  iconClassName?: string;
  /** Custom className for the icon itself */
  iconTypeClassName?: string;
}

/**
 * A reusable component for rendering table cells with temporary visibility toggle.
 * Shows an eye icon when hidden, and displays the actual value when visible.
 * The value automatically hides after a fixed duration (managed by useTemporaryVisibility hook).
 *
 * @example
 * ```tsx
 * const {isVisible, toggleVisibility} = useTemporaryVisibility({duration: 5000});
 *
 * <TemporaryVisibilityCell
 *   id={row.id}
 *   value={row.email}
 *   isVisible={isVisible(row.id)}
 *   onToggle={toggleVisibility}
 *   valueFormatter={decryptInline}
 * />
 * ```
 */
export const TemporaryVisibilityCell: React.FC<TemporaryVisibilityCellProps> = ({
  id,
  value,
  isVisible,
  onToggle,
  valueFormatter,
  textClassName = 'text-ui_input_border_dark max-w-full cursor-pointer hover:opacity-70 transition-opacity text-wrap break-words',
  iconClassName = 'cursor-pointer hover:opacity-70 transition-opacity flex-shrink-0',
  iconTypeClassName = 'text-ui_input_border_dark size-5',
}) => {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggle(id);
  };

  const displayValue = valueFormatter ? valueFormatter(value) : value;

  if (!isVisible) {
    return (
      <button onClick={handleClick} className={iconClassName}>
        <Icon type="eye" className={iconTypeClassName} />
      </button>
    );
  }

  return (
    <Text variant="body" className={textClassName} onClick={handleClick}>
      {displayValue}
    </Text>
  );
};